import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { useTheme } from './ThemeContext';
import { themeTokens } from '@/utils/themeTokens';

export interface ColorTheme {
  primary: string;
  primaryHover: string;
  primaryLight: string;
  secondary: string;
  secondaryHover: string;
  accent: string;
  success: string;
  warning: string;
}

export interface ColorPreset {
  id: string;
  name: string;
  description: string;
  colors: ColorTheme;
}

const defaultTheme: ColorTheme = {
  primary: '225 65% 51%',
  primaryHover: '225 65% 43%',
  primaryLight: '225 65% 96%',
  secondary: '221 22% 43%',
  secondaryHover: '221 22% 35%',
  accent: '220 91% 59%',
  success: '142 71% 45%',
  warning: '48 96% 89%',
};

export const colorPresets: ColorPreset[] = [
  {
    id: 'traditional-red',
    name: 'Traditional Red',
    get description() { return uiText("Classic martial arts red theme"); },
    colors: { ...defaultTheme, primary: '0 85% 27%', primaryHover: '0 85% 23%', primaryLight: '0 85% 95%', secondary: '25 85% 60%', secondaryHover: '25 85% 55%' },
  },
  {
    id: 'modern-blue',
    name: 'Modern Blue',
    get description() { return uiText("Contemporary blue theme"); },
    colors: {
      primary: '220 85% 35%',
      primaryHover: '220 85% 30%',
      primaryLight: '220 85% 95%',
      secondary: '200 85% 60%',
      secondaryHover: '200 85% 55%',
      accent: '280 91% 59%',
      success: '142 71% 45%',
      warning: '48 96% 89%',
    },
  },
  {
    id: 'forest-green',
    name: 'Forest Green',
    get description() { return uiText("Natural green theme"); },
    colors: {
      primary: '140 85% 25%',
      primaryHover: '140 85% 20%',
      primaryLight: '140 85% 95%',
      secondary: '120 85% 45%',
      secondaryHover: '120 85% 40%',
      accent: '160 91% 59%',
      success: '142 71% 45%',
      warning: '48 96% 89%',
    },
  },
  {
    id: 'sunset-orange',
    name: 'Sunset Orange',
    get description() { return uiText("Warm orange theme"); },
    colors: {
      primary: '25 85% 45%',
      primaryHover: '25 85% 40%',
      primaryLight: '25 85% 95%',
      secondary: '35 85% 60%',
      secondaryHover: '35 85% 55%',
      accent: '45 91% 59%',
      success: '142 71% 45%',
      warning: '48 96% 89%',
    },
  },
  {
    id: 'royal-purple',
    name: 'Royal Purple',
    get description() { return uiText("Elegant purple theme"); },
    colors: {
      primary: '280 85% 35%',
      primaryHover: '280 85% 30%',
      primaryLight: '280 85% 95%',
      secondary: '260 85% 60%',
      secondaryHover: '260 85% 55%',
      accent: '240 91% 59%',
      success: '142 71% 45%',
      warning: '48 96% 89%',
    },
  },
];

interface ColorThemeContextType {
  currentTheme: ColorTheme;
  setCustomTheme: (theme: ColorTheme) => Promise<boolean>;
  applyPreset: (presetId: string) => Promise<boolean>;
  resetToDefault: () => Promise<boolean>;
  presets: ColorPreset[];
  isLoading: boolean;
}

const ColorThemeContext = createContext<ColorThemeContextType | undefined>(undefined);

export const ColorThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  useUiLanguage();
  const [currentTheme, setCurrentTheme] = useState<ColorTheme>(defaultTheme);
  const [isLoading, setIsLoading] = useState(true);
  const { profile } = useAuth();
  const { isDark } = useTheme();
  const storageKey = `warriorhub:colour-theme:${profile?.dojo_id || 'public'}`;

  useEffect(() => {
    loadTheme();
  }, [profile]);

  const loadTheme = async () => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Object.keys(defaultTheme).every(key => typeof parsed[key] === 'string' && /^\d+(\.\d+)? \d+(\.\d+)?% \d+(\.\d+)?%$/.test(parsed[key]))) {
          setCurrentTheme(parsed); setIsLoading(false); return;
        }
      }
      if (!profile?.dojo_id) {
        setCurrentTheme(defaultTheme);
        setIsLoading(false);
        return;
      }

      const { data } = await supabase
        .from('dojo_settings')
        .select('*')
        .eq('id', 1)
        .single();

      // Handle the fact that TypeScript types may not be updated yet after migration
      const dojoData = data as any;
      if (dojoData?.color_theme) {
        setCurrentTheme(dojoData.color_theme as ColorTheme);
      } else {
        setCurrentTheme(defaultTheme);
      }
    } catch (error) {
      console.error('Error loading color theme:', error);
      setCurrentTheme(defaultTheme);
    } finally {
      setIsLoading(false);
    }
  };

  const applyTheme = (theme: ColorTheme) => {
    const root = document.documentElement;
    Object.entries(themeTokens(theme,isDark)).forEach(([name,value])=>root.style.setProperty(name,value));
  };

  const setCustomTheme = async (theme: ColorTheme) => {
    setCurrentTheme(theme);
    applyTheme(theme);
    localStorage.setItem(storageKey,JSON.stringify(theme));

    try {
      if (profile?.role === 'admin' && profile?.dojo_id) {
        const { error } = await supabase
          .from('dojo_settings')
          .upsert({
            id: 1,
            color_theme: theme as any,
            updated_at: new Date().toISOString()
          });
        if (error) { console.error('Theme saved on this device; server save failed:',error); return false; }
        return true;
      }
    } catch (error) {
      console.error('Error saving color theme:', error);
    }
    return false;
  };

  const applyPreset = async (presetId: string) => {
    const preset = colorPresets.find(p => p.id === presetId);
    if (preset) {
      return setCustomTheme(preset.colors);
    }
    return false;
  };

  const resetToDefault = () => {
    return setCustomTheme(defaultTheme);
  };

  useEffect(() => {
    applyTheme(currentTheme);
  }, [currentTheme, isDark]);

  const value: ColorThemeContextType = {
    currentTheme,
    setCustomTheme,
    applyPreset,
    resetToDefault,
    presets: colorPresets,
    isLoading,
  };

  return (
    <ColorThemeContext.Provider value={value}>
      {children}
    </ColorThemeContext.Provider>
  );
};

export const useColorTheme = () => {
  const context = useContext(ColorThemeContext);
  if (context === undefined) {
    throw new Error(uiText("useColorTheme must be used within a ColorThemeProvider"));
  }
  return context;
};
